/**
 * Axios instance.
 * - Base URL : `/api` (proxiée par Vite en dev, par nginx en prod).
 * - withCredentials : pour que le cookie de session soit envoyé.
 * - Intercepteur 401 : déclenche un logout via callback registered par AuthContext.
 */

import axios, { AxiosError, type AxiosInstance } from 'axios'

export interface ApiError {
  detail: string
  status?: number
  /**
   * Code applicatif structuré quand le BE renvoie un `detail` dict
   * `{code, message}` (ex : 409 mockup `duplicate_content` /
   * `duplicate_slug`). Permet au call site de distinguer plusieurs
   * sous-cas du même status HTTP sans parser le message FR.
   */
  code?: string
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

let logoutCallback: (() => void) | null = null

export function registerLogoutCallback(cb: () => void): void {
  logoutCallback = cb
}

/**
 * Normalise une erreur Axios en `ApiError` stable côté FE.
 *
 * - Dépile `error.response.data.detail` quand FastAPI renvoie une liste
 *   d'objets `{loc, msg, type}` (validation Pydantic) — on prend le 1er msg.
 * - Dépile aussi un dict `{code, message}` (ex: 409 mockup) et expose
 *   le `code` au call site pour distinguer les sous-cas par status.
 * - Fallback en cascade : detail (string) → error.message → texte générique.
 *
 * Exposée pour test unitaire ; la même fonction est appelée par
 * l'intercepteur de réponse ci-dessous.
 */
export function transformAxiosError(error: AxiosError<ApiError>): ApiError {
  let detail: unknown = error.response?.data?.detail
  let code: string | undefined

  if (
    detail !== null &&
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    'message' in detail
  ) {
    // FastAPI HTTPException(detail={"code": "...", "message": "..."})
    const obj = detail as { code?: unknown; message?: unknown }
    if (typeof obj.code === 'string') code = obj.code
    detail = typeof obj.message === 'string' ? obj.message : ''
  } else if (Array.isArray(detail)) {
    detail = (detail as Array<{ msg?: string }>)[0]?.msg ?? 'Erreur de validation'
  }

  return {
    // `||` (et pas `??`) : on traite la chaîne vide comme absente, sinon
    // un AxiosError sans payload + sans message renvoie un détail vide à
    // l'UI (toast `''`).
    detail:
      (typeof detail === 'string' ? detail : '') || error.message || 'Une erreur est survenue',
    status: error.response?.status,
    code,
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Routes publiques qui ne doivent JAMAIS forcer une redirection sur 401.
 * Le boot d'AuthContext appelle `/auth/me` pour detecter une session
 * existante : sur la landing ou la page privacy, un 401 est attendu pour
 * un visiteur anonyme et doit rester silencieux. La redirection vers
 * `/atelier` ne s'applique qu'aux routes protegees ou l'utilisateur etait
 * cense avoir une session.
 */
const PUBLIC_PATHS = new Set(['/', '/privacy', '/sgraphiccreations-privacy', '/atelier'])

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname
      if (logoutCallback) logoutCallback()
      if (!PUBLIC_PATHS.has(path)) {
        window.location.href = '/atelier'
      }
    }

    return Promise.reject(transformAxiosError(error))
  },
)
